<template>
  <v-dialog
    :value="show"
    content-class="inquiry-dialog"
    max-width="900"
    overlay-color="black"
    overlay-opacity="0.5"
    persistent
    scrollable
  >
    <v-card class="inquiry-dialog__card" data-cy-inquiry-dialog>
      <v-card-title class="inquiry-dialog__header">
        <span class="headline">{{ $t('inquiry_title') }}</span>
        <v-btn text depressed @click="closeDialogs">
          <span class="text-decoration-underline">{{ $t('close') }}</span>
        </v-btn>
      </v-card-title>

      <v-card-text class="inquiry-dialog__content">
        <div v-if="formLoading" class="inquiry-dialog__status" role="status">
          <v-progress-circular indeterminate color="accent" />
          <span>{{ $t('hubspotFormStatus.loading') }}</span>
        </div>

        <div v-else-if="formError" class="inquiry-dialog__status" role="alert">
          <span>{{ $t('hubspotFormStatus.loadError') }}</span>
          <v-btn color="accent" rounded depressed @click="retryForm">
            {{ $t('hubspotFormStatus.retry') }}
          </v-btn>
        </div>

        <div v-show="!formLoading && !formError" class="inquiry-dialog__form" data-cy-hubspot-form>
          <div :id="formTargetId" ref="hubspotFormContainer"></div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { hasOptionalConsent } from '@/analytics/consent'
import { loadHubspotForms, loadHubspotTracking } from '@/integrations/hubspot'

const HUBSPOT_PORTAL_ID = '5157245'
const FORM_READY_TIMEOUT = 15000
const PREFILLED_FIELDS = [
  'subscription_package',
  'mesi_configurator_url',
  'mtablet_extensions_and_modules',
  'mtablet_applications',
  'mtablet_accessories',
  'layouts'
]
const HIDDEN_FIELD_SELECTORS = PREFILLED_FIELDS.map(name => `.hs_${name}`)
const FORM_SYNC_DELAYS = [0, 100, 500]

export default {
  name: 'SendInquiryDialog',
  props: {
    show: Boolean
  },
  computed: {
    ...mapGetters([
      'configURL',
      'selectedExtensionsModules',
      'selectedApplications',
      'selectedAccessories',
      'selectedLayout'
    ]),
    formId () {
      return this.$t('inquiryForm')
    }
  },
  data () {
    return {
      formTargetId: 'hubspot-inquiry-form',
      formLoading: false,
      formError: false,
      formElement: null,
      formPromise: null,
      renderedFormId: null,
      formGeneration: 0,
      formReadyTimeout: null,
      formSyncTimeouts: [],
      submissionHandled: false
    }
  },
  watch: {
    show (visible) {
      if (visible) {
        this.$nextTick(() => this.openForm())
      }
    },
    formId (nextFormId, previousFormId) {
      if (nextFormId === previousFormId) {
        return
      }

      this.resetForm()
      if (this.show) {
        this.$nextTick(() => this.openForm())
      }
    }
  },
  mounted () {
    if (this.show) {
      this.$nextTick(() => this.openForm())
    }
  },
  beforeDestroy () {
    this.clearFormReadyTimeout()
    this.clearFormSyncTimeouts()
    this.formGeneration += 1
  },
  methods: {
    closeDialogs () {
      this.$store.commit('CLOSE_ALL_DIALOGS')
    },
    openForm () {
      if (hasOptionalConsent()) {
        loadHubspotTracking().catch(() => {})
      }

      return this.ensureForm().then(() => {
        this.synchronizeForm()
      }).catch(() => {})
    },
    ensureForm () {
      if (this.formElement && this.renderedFormId === this.formId) {
        return Promise.resolve(this.formElement)
      }

      if (this.formPromise) {
        return this.formPromise
      }

      this.formLoading = true
      this.formError = false
      const generation = this.formGeneration
      const formId = this.formId

      this.formPromise = loadHubspotForms()
        .then(() => this.createForm(formId, generation))
        .catch(error => {
          if (generation === this.formGeneration) {
            this.formLoading = false
            this.formError = true
            this.formPromise = null
            this.trackEvent('SendInquiry', 'Forms', 'load failed')
          }
          throw error
        })

      return this.formPromise
    },
    createForm (formId, generation) {
      return new Promise((resolve, reject) => {
        if (!window.hbspt || !window.hbspt.forms || !this.$refs.hubspotFormContainer) {
          reject(new Error('HubSpot form is unavailable'))
          return
        }

        this.formReadyTimeout = window.setTimeout(() => {
          reject(new Error('HubSpot form timed out'))
        }, FORM_READY_TIMEOUT)

        window.hbspt.forms.create({
          portalId: HUBSPOT_PORTAL_ID,
          formId,
          target: `#${this.formTargetId}`,
          onFormReady: form => {
            if (generation !== this.formGeneration) {
              return
            }

            const formElement = this.getFormElement(form)
            if (!formElement) {
              this.clearFormReadyTimeout()
              reject(new Error('HubSpot form element is unavailable'))
              return
            }

            this.clearFormReadyTimeout()
            this.formElement = formElement
            this.renderedFormId = formId
            this.formLoading = false
            this.formError = false
            this.submissionHandled = false
            this.synchronizeForm()
            resolve(formElement)
          },
          onFormSubmitted: () => {
            if (generation === this.formGeneration) {
              this.handleSubmitted()
            }
          }
        })
      })
    },
    getFormElement (form) {
      if (form && typeof form.get === 'function') {
        return form.get(0)
      }
      if (form && form[0]) {
        return form[0]
      }
      if (form && typeof form.querySelectorAll === 'function') {
        return form
      }
      return this.$refs.hubspotFormContainer
        ? this.$refs.hubspotFormContainer.querySelector('form') || this.$refs.hubspotFormContainer
        : null
    },
    getSearchRoots () {
      const roots = []
      const addRoot = root => {
        if (root && typeof root.querySelectorAll === 'function' && !roots.includes(root)) {
          roots.push(root)
        }
      }
      const addFrameDocument = frame => {
        try {
          addRoot(frame.contentDocument)
          addRoot(frame.contentDocument && frame.contentDocument.body)
        } catch (error) {
          // Cross-origin frames are not inspectable; onFormReady still supplies the form root.
        }
      }

      addRoot(this.formElement)
      addRoot(this.$refs.hubspotFormContainer)

      roots.slice().forEach(root => {
        if (root.tagName === 'IFRAME') {
          addFrameDocument(root)
        }
        Array.from(root.querySelectorAll('iframe')).forEach(addFrameDocument)
      })

      return roots
    },
    getFields (name) {
      const fields = new Set()
      this.getSearchRoots().forEach(root => {
        Array.from(root.querySelectorAll(`[name="${name}"]`)).forEach(field => fields.add(field))
      })
      return Array.from(fields)
    },
    synchronizeForm () {
      this.prefillForm()
      this.scheduleFormSynchronization()
    },
    scheduleFormSynchronization () {
      this.clearFormSyncTimeouts()
      const generation = this.formGeneration
      this.formSyncTimeouts = FORM_SYNC_DELAYS.map(delay => window.setTimeout(() => {
        if (generation === this.formGeneration) {
          this.prefillForm()
        }
      }, delay))
    },
    prefillForm () {
      if (!this.getSearchRoots().length) {
        return false
      }

      this.hidePrefilledFields()
      this.setFieldValue('subscription_package', '')
      this.setFieldValue('mesi_configurator_url', this.configURL || window.location.href)
      this.setCheckedValues('mtablet_extensions_and_modules', this.selectedExtensionsModules)
      this.setCheckedValues('mtablet_applications', this.selectedApplications)
      this.setCheckedValues('mtablet_accessories', this.selectedAccessories)
      this.setCheckedValues('layouts', this.selectedLayout ? [this.selectedLayout] : [])
      PREFILLED_FIELDS.forEach(this.hideField)
      return true
    },
    hidePrefilledFields () {
      this.getSearchRoots().forEach(root => {
        HIDDEN_FIELD_SELECTORS.forEach(selector => {
          Array.from(root.querySelectorAll(selector)).forEach(wrapper => {
            wrapper.style.display = 'none'
          })
        })
      })
    },
    setFieldValue (name, value) {
      this.getFields(name).forEach(field => {
        field.value = value || ''
        this.emitFieldEvents(field)
      })
    },
    setCheckedValues (name, values = []) {
      const selectedValues = new Set(values.map(value => String(value)))
      this.getFields(name).forEach(field => {
        const shouldBeChecked = selectedValues.has(String(field.value))
        if (field.checked === shouldBeChecked) {
          return
        }

        field.click()
        if (field.checked !== shouldBeChecked) {
          field.checked = shouldBeChecked
          this.emitFieldEvents(field)
        }
      })
    },
    emitFieldEvents (field) {
      const eventWindow = field.ownerDocument.defaultView || window
      field.dispatchEvent(new eventWindow.Event('input', { bubbles: true }))
      field.dispatchEvent(new eventWindow.Event('change', { bubbles: true }))
    },
    hideField (name) {
      this.getFields(name).forEach(field => {
        const wrapper = field.closest('.hs-form-field') || field.closest('fieldset')
        if (wrapper && wrapper !== this.formElement) {
          wrapper.style.display = 'none'
        } else if (field.type !== 'hidden' && field.parentElement && field.parentElement !== this.formElement) {
          field.parentElement.style.display = 'none'
        }
      })
    },
    handleSubmitted () {
      if (this.submissionHandled) {
        return
      }

      this.submissionHandled = true
      this.trackEvent('SendInquiry', 'Forms', 'OK')
      this.$store.dispatch('SHOW_DIALOG_THANK_YOU')
      this.$nextTick(() => this.resetForm())
    },
    retryForm () {
      this.resetForm()
      this.$nextTick(() => this.openForm())
    },
    resetForm () {
      this.clearFormReadyTimeout()
      this.clearFormSyncTimeouts()
      this.formGeneration += 1
      this.formElement = null
      this.formPromise = null
      this.renderedFormId = null
      this.formLoading = false
      this.formError = false
      this.submissionHandled = false

      if (this.$refs.hubspotFormContainer) {
        this.$refs.hubspotFormContainer.innerHTML = ''
      }
    },
    clearFormReadyTimeout () {
      if (this.formReadyTimeout) {
        window.clearTimeout(this.formReadyTimeout)
        this.formReadyTimeout = null
      }
    },
    clearFormSyncTimeouts () {
      this.formSyncTimeouts.forEach(timeout => window.clearTimeout(timeout))
      this.formSyncTimeouts = []
    }
  }
}
</script>

<style lang="scss">
.inquiry-dialog {
  overflow: hidden;

  .inquiry-dialog__card {
    max-height: 90vh;
  }

  .inquiry-dialog__header {
    display: flex;
    flex: 0 0 auto;
    justify-content: space-between;
    gap: 16px;
    word-break: normal;
  }

  .inquiry-dialog__content {
    min-height: 240px;
    padding: 24px !important;
    background: #fff;
  }

  .inquiry-dialog__status {
    display: flex;
    min-height: 190px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }

  .inquiry-dialog__form,
  .hbspt-form,
  iframe {
    width: 100% !important;
    max-width: 100% !important;
  }

  // These fields carry the configuration to HubSpot and are never user-editable.
  // Keep them hidden even while HubSpot is still rendering or re-rendering the form.
  .hs_subscription_package,
  .hs_mesi_configurator_url,
  .hs_layouts,
  .hs_mtablet_extensions_and_modules,
  .hs_mtablet_applications,
  .hs_mtablet_accessories {
    display: none !important;
  }
}

@media (max-width: 600px) {
  .inquiry-dialog {
    margin: 12px;

    .inquiry-dialog__header,
    .inquiry-dialog__content {
      padding: 16px !important;
    }
  }
}
</style>
